import { Dispatch, SetStateAction } from 'react';
import { Dayjs } from 'dayjs';
import { BigNum, QUOTE_PRECISION_EXP } from '@drift-labs/sdk';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import { signDependentValue } from 'src/utils/signDependentValue';
import { cj } from 'src/utils/classNames';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { CalendarPnlData } from 'src/components/PortfolioPage/PortfolioTradeSubPage/utils/buildCalendarPnlData';
import PortfolioTradeCalendarHeader from 'src/components/PortfolioPage/PortfolioTradeSubPage/PortfolioTradeCalendar/PortfolioTradeCalendarHeader';
import PortfolioTradeCalendarTooltipContent from 'src/components/PortfolioPage/PortfolioTradeSubPage/PortfolioTradeCalendar/PortfolioTradeCalendarTooltipContent';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const GRID_CLASSNAME = 'grid grid-cols-7';
const GAP_CLASSNAME = 'gap-2xs sm:gap-xs';

interface Props {
	data: CalendarPnlData;
	lastThreeMonths: Dayjs[];
	monthIndex: number;
	setMonthIndex: Dispatch<SetStateAction<number>>;
	isLoading: boolean;
}

export default function PortfolioTradeCalendar({
	data,
	lastThreeMonths,
	monthIndex,
	setMonthIndex,
	isLoading,
}: Props) {
	const isMobileScreenSize = useIsMobileScreenSize();

	const {
		daysInMonth,
		startWeekday,
		pnlByDay,
		numWinningDays,
		numLosingDays,
		numFlatDays,
	} = data;

	return (
		<>
			{/* Month selector and daily stats. */}
			<PortfolioTradeCalendarHeader
				monthIndex={monthIndex}
				setMonthIndex={setMonthIndex}
				lastThreeMonths={lastThreeMonths}
				isLoading={isLoading}
				numWinningDays={numWinningDays}
				numLosingDays={numLosingDays}
				numFlatDays={numFlatDays}
			/>

			{/* Calendar grid. */}
			<div
				className={cj(
					'flex grow flex-col',
					isLoading && 'animate-pulse',
					GAP_CLASSNAME
				)}
				// Remount when month changes so we don't get any carry over state from previous month.
				key={monthIndex}
			>
				{/* We render the week header and days in two separate grids so that we can */}
				{/* allow the days to uniformly and responsively fill the available space. */}
				<div className={cj('grid-rows-[16px]', GRID_CLASSNAME, GAP_CLASSNAME)}>
					{DAYS_OF_WEEK.map((day) => (
						<div key={day} className="text-center typo-b4 text-text-secondary">
							{day.slice(0, 1)}
						</div>
					))}
				</div>

				<div className={cj('grow auto-rows-fr', GRID_CLASSNAME, GAP_CLASSNAME)}>
					{/* Align the start of the month to the correct day of the week. */}
					{Array.from({ length: startWeekday }).map((_, i) => (
						<div key={`offset-${i}`} />
					))}

					{Array.from({ length: daysInMonth }).map((_, index) => {
						const day = index + 1;
						const { pnl, feesAndFunding, isMockedSnapshot } = pnlByDay.get(
							day
						) ?? {
							pnl: 0,
							feesAndFunding: undefined,
						};

						const bgClassName = signDependentValue(pnl, {
							positive: 'bg-positive-green-secondary-bg',
							negative: 'bg-negative-red-secondary-bg',
							zero: '',
						});

						const pnlColorClassName = signDependentValue(pnl, {
							positive: 'text-text-positive-green-button',
							negative: 'text-text-negative-red-button',
							zero: 'text-text-default',
						});

						const feesAndFundingColorClassName = signDependentValue(
							feesAndFunding,
							{
								positive: 'text-text-positive-green-button',
								negative: 'text-text-negative-red-button',
								zero: 'text-text-default',
							}
						);

						const isPnlZero = pnl === 0;

						return (
							<Tooltip
								key={day}
								decoration="none"
								className={cj(
									'flex h-11 flex-col items-center justify-center gap-y-xs rounded-xs sm:h-auto',
									'cursor-default text-text-default transition-colors',
									bgClassName,
									isMockedSnapshot &&
										'hover:cursor-help hover:bg-input-bg hover:text-text-disabled'
								)}
								disabled={isPnlZero && !isMockedSnapshot}
								contentClassName="flex flex-col gap-y-xs min-w-50"
								content={
									<PortfolioTradeCalendarTooltipContent
										pnl={pnl}
										feesAndFunding={feesAndFunding}
										pnlColorClassName={pnlColorClassName}
										feesAndFundingColorClassName={feesAndFundingColorClassName}
										isMockedSnapshot={isMockedSnapshot}
									/>
								}
							>
								<span className="typo-t4 sm:typo-t3">{day}</span>

								{!isPnlZero && (
									<span className={cj('typo-b5 sm:typo-b4', pnlColorClassName)}>
										<NumberDisplay
											value={BigNum.fromPrint(
												pnl.toString(),
												QUOTE_PRECISION_EXP
											)}
											type={
												// We hide the $ sign on mobile to preserve space.
												isMobileScreenSize ? 'number_signed' : 'currency_signed'
											}
											decimals={2}
											millify={{ rounded: true }}
											className={pnlColorClassName}
											isAccountValueToHide
										/>
									</span>
								)}
							</Tooltip>
						);
					})}
				</div>
			</div>
		</>
	);
}
